import * as React from "react";
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  ChipProps,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { isOpenStatus } from "../utils/statuses";

type ProviderContact = {
  status?: "UNLOCKED" | "INVITED" | "PENDING_PROVIDER" | "UNKNOWEN" | string;
  email?: string | null;
  phone?: string | null;
  emailMasked?: string | null;
  phoneMasked?: string | null;
  updatedAt?: any;
  leadStatus?: string
};

type BusinessLite = {
  name?: string | null;
  address?: string | null;
  street?: string | null;
  city?: { name?: string | null } | null;
  State?: { name?: string | null; state_code?: string | null } | null;
  zipcode?: string | null;
  emails?: string[] | string | null;
  phoneNumbers?:
    | Array<{ phoneNumber?: string | null; countryCode?: string | null }>
    | null;
};

export function ProviderContactPanel({
  lead,
  children,
  defaultExpanded = false,
  expanded,
  onChange,
  showProviderContact = false,
}: {
  /** Expected shape: { providerContact?:..., business?:{name,address}? } */
  lead: {
    providerContact?: ProviderContact | null;
    business?: BusinessLite | null;
    status: string
  };
  /** Optional content rendered below contact rows (e.g., SettlementInline) */
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
  showProviderContact?: boolean;
}) {
  const pc = lead?.providerContact || {};
  const showProviderAfterSendingAgrement =
    pc?.status !== "PENDING_PROVIDER" && showProviderContact;
  const businessName = String(lead?.business?.name || "").trim() || null;
  const businessEmail = React.useMemo(() => {
    if (!showProviderAfterSendingAgrement) return null;
    const emails = lead?.business?.emails;
    if (Array.isArray(emails)) {
      return String(emails.find(Boolean) || "").trim() || null;
    }
    return String(emails || "").trim() || null;
  }, [lead?.business?.emails, showProviderAfterSendingAgrement]);

  const businessPhone = React.useMemo(() => {
    if (!showProviderAfterSendingAgrement) return null;
    const firstPhone = lead?.business?.phoneNumbers?.find(
      (item) => String(item?.phoneNumber || "").trim()
    );
    if (!firstPhone) return null;
    const countryCode = String(firstPhone.countryCode || "").trim();
    const phoneNumber = String(firstPhone.phoneNumber || "").trim();
    return `${countryCode}${countryCode && phoneNumber ? " " : ""}${phoneNumber}`.trim() || null;
  }, [lead?.business?.phoneNumbers, showProviderAfterSendingAgrement]);

  const isLeadOpen = isOpenStatus(lead?.status || "")
  const status: string = isLeadOpen ? pc?.status ?? "UNKNOWEN" : "UNKNOWEN"
  const email = businessEmail ?? pc?.email ?? pc?.emailMasked ?? "";
  const phone = businessPhone ?? pc?.phone ?? pc?.phoneMasked ?? "";
  const addr = React.useMemo(() => {
    const directAddress = String(lead?.business?.address || "").trim();
    if (directAddress) return directAddress;
    const street = String(lead?.business?.street || "").trim();
    const city = String(lead?.business?.city?.name || "").trim();
    const state =
      String(
        lead?.business?.State?.state_code || lead?.business?.State?.name || ""
      ).trim();
    const zipcode = String(lead?.business?.zipcode || "").trim();
    return [street, city, state, zipcode].filter(Boolean).join(", ");
  }, [
    lead?.business?.State?.name,
    lead?.business?.State?.state_code,
    lead?.business?.address,
    lead?.business?.city?.name,
    lead?.business?.street,
    lead?.business?.zipcode,
  ]);

  const copy = async (v?: string | null) => {
    if (!v) return;
    try {
      await navigator.clipboard.writeText(v);
    } catch { }
  };
  const mapsHref = (a: string) =>
    `https://maps.google.com/?q=${encodeURIComponent(a)}`;

  const Row = ({
    label,
    value,
    actions,
    mono = false,
  }: {
    label: string;
    value?: string | null;
    actions?: React.ReactNode;
    mono?: boolean;
  }) => {
    if (!value) return null;
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{
          py: 0.75,
          borderBottom: "1px dashed",
          borderColor: "divider",
          "&:last-of-type": { borderBottom: "none" },
        }}
      >
        <Typography
          variant="body2"
          sx={{ minWidth: 88, color: "text.secondary" }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-word",
            fontFamily: mono
              ? "ui-monospace, SFMono-Regular, Menlo, monospace"
              : undefined,
          }}
        >
          {value}
        </Typography>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ ml: "auto" }}
          alignItems="center"
        >
          {actions}
        </Stack>
      </Stack>
    );
  };
  type Status = "UNLOCKED" | "INVITED" | "PENDING_PROVIDER" | "UNKNOWEN";

  const STATUS_META: Record<
    Status,
    { label: string; color: ChipProps["color"]; tip: string }
  > = {
    UNLOCKED: {
      label: "Agreement Accepted",
      color: "success",
      tip: "The provider accepted the referral agreement and contact is now shared.",
    },
    INVITED: {
      label: "Invited",
      color: "info",
      tip: "Invitation sent to care provider — waiting on response.",
    },
    PENDING_PROVIDER: {
      label: "Pending",
      color: "warning",
      tip: "Awaiting approval or action.",
    },

    UNKNOWEN: {
      label: "Closed",
      color: "default",
      tip: "",
    },
  };

  type StatusChipProps = Omit<ChipProps, "label" | "color"> & {
    /** Current status */
    status: Status | string;
    /** Optional override for the tooltip text */
    tip?: string;
    /** Optional override for the label text */
    labelOverride?: string;
  };

  /**
   * StatusChip
   * - Shows a colored Chip for a status
   * - Tooltip appears when hovering the label text
   * - You can still pass normal Chip props (clickable, onClick, sx, etc.)
   */
  function StatusChip({
    status,
    tip,
    labelOverride,
    size = "small",
    variant = "outlined",
    ...chipProps
  }: StatusChipProps) {
    // Fallback unknown statuses to PENDING styling
    const meta = (STATUS_META as any)[status];

    const finalLabel = labelOverride ?? meta.label;
    const finalTip = tip ?? meta.tip;

    return (
      <Chip
        {...chipProps}
        size={size}
        variant={variant}
        color={meta.color}
        label={
          <Tooltip title={finalTip} arrow>
            {/* Tooltip needs an element child */}
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {finalLabel}
            </span>
          </Tooltip>
        }
      />
    );
  }
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onChange={(_event, nextExpanded) => onChange?.(nextExpanded)}
      disableGutters
      sx={{ bgcolor: "background.paper", borderRadius: 1 }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Provider
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <StatusChip
            status={status}
            clickable
            onClick={() => console.log("chip clicked")}
            sx={{ ml: 1 }}
          />
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0.5 }}>
        {businessName && (
          <Row
            label="Name"
            value={businessName}
          />
        )}

        {/* Email */}
        {email && (
          <Row
            label="Email"
            value={email}
            mono
            actions={
              <>
                <Tooltip title="Email">
                  <IconButton
                    size="small"
                    component="a"
                    href={`mailto:${email}`}
                  >
                    <EmailOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy email">
                  <IconButton size="small" onClick={() => copy(email)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        )}

        {/* Phone */}
        {phone && (
          <Row
            label="Phone"
            value={phone}
            mono
            actions={
              <>
                <Tooltip title="Call">
                  <IconButton size="small" component="a" href={`tel:${phone}`}>
                    <PhoneOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy phone">
                  <IconButton size="small" onClick={() => copy(phone)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        )}

        {/* Address (if available) */}
        {addr && (
          <Row
            label="Address"
            value={addr}
            actions={
              <>
                <Tooltip title="Open in Google Maps">
                  <IconButton
                    size="small"
                    component="a"
                    href={mapsHref(addr)}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <MapOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy address">
                  <IconButton size="small" onClick={() => copy(addr)}>
                    <ContentCopyIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            }
          />
        )}

        {!email && !phone && !addr && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Contact details will appear once the provider shares access.
          </Alert>
        )}

        {/* Slot for extra inline content (e.g., SettlementInline) */}
        {children ? <Box sx={{ mt: 1.25 }}>{children}</Box> : null}
      </AccordionDetails>
    </Accordion>
  );
}
